import type { Rating, ProgressMode } from "../types/rating";
import { dateValueToDate } from "../types/data";
import { hasImportMarker } from "./gameProgressEligibility";

export const RATING_PROGRESS_MODE_EVENT = "rating-progress-mode-request";
export const RATING_PROGRESS_MODES = {
  ARCHIVE: "archive",
  CURRENT: "current"
} as const;

export const isValidRatingProgressMode = (mode: unknown): mode is ProgressMode =>
  mode === RATING_PROGRESS_MODES.ARCHIVE || mode === RATING_PROGRESS_MODES.CURRENT;

export const getRatingProgressDayKey = (date: Date | string | number = new Date()): string => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return getRatingProgressDayKey(new Date());

  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0")
  ].join("-");
};

export const requestRatingProgressMode = () =>
  new Promise<ProgressMode>((resolve) => {
    if (typeof window === "undefined") {
      resolve("current");
      return;
    }

    window.dispatchEvent(new CustomEvent(RATING_PROGRESS_MODE_EVENT, {
      detail: { resolve }
    }));
  });

export const shouldConfirmRatingMode = (savedMode: unknown = null) => !isValidRatingProgressMode(savedMode);

export const hasSevenRatingsForDay = (ratings: Rating[] = [], nextRatedAt = new Date()) => {
  const date = nextRatedAt instanceof Date ? nextRatedAt : new Date(nextRatedAt);
  if (Number.isNaN(date.getTime())) return false;

  const dayKey = getRatingProgressDayKey(date);
  const countForDay = ratings.filter((rating: Rating) => {
    if (hasImportMarker(rating)) return false;
    if (rating?.streakEligible === false) return false;
    const value = rating?.ratedAt || rating?.firstRatedAt;
    const ratingDate = dateValueToDate(value);
    return ratingDate !== null && getRatingProgressDayKey(ratingDate) === dayKey;
  }).length;

  return countForDay >= 7;
};
